import {useState, useEffect} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@sakura-grp/ui/drawer";
import {Button} from "@sakura-grp/ui/button";
import {Input} from "@sakura-grp/ui/input";
import {Label} from "@sakura-grp/ui/label";
import {toast} from "@sakura-grp/ui/sonner";
import {getCompanyUsage, renameCompany} from "@/lib/api/business-cards";
import {useKeyboardInset} from "@/hooks/useKeyboardInset";

interface CompanyRenameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 対象名刺。サーバ側でこの名刺の会社を解決してリネームする */
  cardId: string;
  /** 現在の会社名（入力の初期値・件数文言のフォールバック） */
  currentName: string;
  /** リネーム成功時（新名）。呼び出し側でローカル反映・シートを閉じる等に使う */
  onRenamed: (newName: string) => void;
}

/**
 * 会社名の「全体修正（リネーム）」。会社マスタの登録名そのものを直す明示操作。
 * 影響件数（名刺・顧客）を提示してから確定させ、誤って共有会社を全社改名する事故を防ぐ。
 * 「別の会社に変えたい」だけなら付け替え（CompanySelectSheet）を使う。
 */
export function CompanyRenameDialog({
  open,
  onOpenChange,
  cardId,
  currentName,
  onRenamed,
}: CompanyRenameDialogProps) {
  const [newName, setNewName] = useState(currentName);
  const keyboardInset = useKeyboardInset();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) setNewName(currentName);
  }, [open, currentName]);

  const {data: usage} = useQuery({
    queryKey: ["company-usage", cardId],
    queryFn: () => getCompanyUsage(cardId),
    enabled: open,
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: () => renameCompany(cardId, newName),
    onSuccess: (r) => {
      toast.success(`会社名を全体修正しました（名刺${r.cards}枚・顧客${r.customers}人に反映）`);
      // 全体に波及するので顧客詳細・一覧・件数を広く再取得
      queryClient.invalidateQueries({queryKey: ["customer-detail"]});
      queryClient.invalidateQueries({queryKey: ["tab-customers"]});
      queryClient.invalidateQueries({queryKey: ["tab-counts"]});
      onRenamed(r.newName);
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const trimmed = newName.trim();
  const canSave = trimmed.length > 0 && trimmed !== currentName.trim() && !mutation.isPending;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent
        className="px-lg pb-safe-min"
        style={keyboardInset > 0 ? {paddingBottom: keyboardInset} : undefined}
      >
        <DrawerHeader className="pb-md">
          <DrawerTitle>会社名を全体修正</DrawerTitle>
          <DrawerDescription>
            会社の<span className="font-bold">登録名そのもの</span>を直します。この会社を使う
            すべての名刺・顧客に反映されます。
          </DrawerDescription>
        </DrawerHeader>

        <div className="space-y-md">
          <div className="space-y-sm">
            <Label htmlFor="rename-company-input">会社名</Label>
            <Input
              id="rename-company-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="正しい会社名"
              className="text-base"
              autoFocus
            />
          </div>
          <p className="text-muted-foreground text-sm">
            {usage
              ? `「${usage.companyName ?? currentName}」は 名刺${usage.cards}枚・顧客${usage.customers}人 で使われています。`
              : "影響範囲を確認しています…"}
          </p>
          <p className="text-muted-foreground text-xs">
            この人だけ別の会社に変えたい場合は、閉じて「会社」から付け替えてください（全体には反映されません）。
          </p>
        </div>

        <DrawerFooter className="p-0 pt-md flex flex-col gap-md">
          <Button onClick={() => mutation.mutate()} disabled={!canSave}>
            {mutation.isPending
              ? "修正中…"
              : usage
                ? `全体修正する（名刺${usage.cards}枚・顧客${usage.customers}人に反映）`
                : "全体修正する"}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            やめる
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
